import React, { useEffect, useState } from "react";
import { SignupSubscriptionModal } from "../../components/SignupSubscriptionModal";
import { CircularProgress } from "@mui/material";
import {
  useResendVerificationLink,
  useVerifyEmailLink,
} from "../../services/mutations";
import { useSearchParams, useNavigate } from "react-router-dom";
import { styled } from "@mui/material";
import OTPModal from "../../components/PasswordAlert";
import TextView from "../../components/TextView";
import Button from "../../components/Button";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { userProfileSuccess } from "../../redux/actions/user";
import { getActiveSubscription } from "../../redux/actions/subscription";

const LoadingWrapper = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100vh",
}));

export const GradientContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
  background: `
    conic-gradient(
      from 180deg at 50% 50%, 
      #F5EEDE -41.73deg, 
      #CCFEFF 37.8deg, 
      #F7FED5 140.4deg, 
      #FDCFFE 216deg, 
      #F5EEDE 318.27deg, 
      #CCFEFF 397.8deg
    ),
    linear-gradient(
      0deg, 
      rgba(255, 255, 255, 0.5), 
      rgba(255, 255, 255, 0.5)
    )
  `,
  backgroundBlendMode: "overlay",
}));

export const ButtonContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "end",
  marginTop: "16px",
}));

export default function Onboarding() {
  const [loading, setLoading] = useState(true);
  const [subscriptionModal, setSubscriptionModal] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showResendPopup, setShowResendPopup] = useState(false);
  const [resendSuccessPopup, setResendSuccessPopup] = useState(false);
  const [userId, setUserId] = useState(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const verify = useVerifyEmailLink();
  const resend = useResendVerificationLink();

  const key = searchParams.get("key");
  const user_key = searchParams.get("user_key");

  const clearSearchParams = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("key");
    newParams.delete("user_key");
    navigate(`/onboarding?${newParams.toString()}`, { replace: true });
  };

  const verifyUser = async () => {
    try {
      if (!key || !user_key) {
        return;
      }

      verify.mutateAsync(
        {
          key: key,
          user_key: user_key,
        },
        {
          onSuccess: (response) => {
            const user = response?.data?.data?.user;
            let token = response?.data?.data?.token;

            // Store user ID and user data
            setUserId(user.id);

            // Save to localStorage for app-wide access
            localStorage.setItem("keyCloak", JSON.stringify({ token, user }));

            // Update Redux store with user profile
            dispatch(userProfileSuccess(user));

            // Fetch active subscription data
            dispatch(getActiveSubscription());

            // Set walkthrough flag if needed (similar to login flow)
            localStorage.setItem("show_walkthrough", "true");

            clearSearchParams();

            setLoading(false);
            setSubscriptionModal(true);
          },
          onError: (error) => {
            if (!error.response?.data?.success) {
              setLoading(false);
              setSubscriptionModal(false);
              setShowResendPopup(true);
            }
          },
        }
      );
    } catch (err) {
      console.log("err", err);
    }
  };

  const resendLink = async () => {
    if (!key || !user_key) {
      return;
    }

    resend.mutateAsync(
      {
        user_key: user_key,
      },
      {
        onSuccess: (response) => {
          setShowResendPopup(false);
          setResendSuccessPopup(true);
          clearSearchParams();
        },
        onError: (error) => {
          toast.error("Error sending verification link. Please try again.");
        },
      }
    );
  };

  useEffect(() => {
    if (key && user_key) {
      verifyUser();
    } else {
      setLoading(false);
      if (!resendSuccessPopup) {
        setSubscriptionModal(true);
      }
    }
  }, [key, user_key]);

  // Prevent body scroll on mobile when modal is open
  useEffect(() => {
    if (subscriptionModal) {
      // Save current scroll position
      const scrollY = window.scrollY;
      const body = document.body;
      const html = document.documentElement;
      
      // Prevent scroll
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = `-${scrollY}px`;
      body.style.width = "100%";
      body.style.height = "100%";
      html.style.overflow = "hidden";
      html.style.height = "100%";
      
      return () => {
        // Restore scroll
        body.style.overflow = "";
        body.style.position = "";
        body.style.top = "";
        body.style.width = "";
        body.style.height = "";
        html.style.overflow = "";
        html.style.height = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [subscriptionModal]);

  return (
    <GradientContainer>
      {(loading || subscriptionLoading) && (
        <LoadingWrapper>
          <CircularProgress />
        </LoadingWrapper>
      )}

      <SignupSubscriptionModal
        open={subscriptionModal}
        handleCloseModal={() => setSubscriptionModal(false)}
        userId={userId}
        onLoadingChange={setSubscriptionLoading}
      />

      <OTPModal
        open={showResendPopup || resendSuccessPopup}
        fromSignup={true}
        title={
          showResendPopup
            ? "Verification Link Expired"
            : "Verification Link Sent"
        }
      >
        <TextView>
          {showResendPopup
            ? "It looks like your email verification link is either invalid or has expired. But don't worry! You can request a new verification link."
            : "A new verification link has been sent to your email. Please check your inbox and click on the link to verify your email."}
        </TextView>

        {showResendPopup && (
          <ButtonContainer>
            <Button
              style={{ width: "auto" }}
              onClick={resendLink}
              isLoading={resend.isLoading}
            >
              Send Verification Link
            </Button>
          </ButtonContainer>
        )}
      </OTPModal>
    </GradientContainer>
  );
}
